import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, User, Loader2, Check, AlertCircle, ArrowLeft, Send } from 'lucide-react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

/**
 * Page Contact publique — formulaire stylé Editorial Magazine.
 *
 * Sécurité :
 *   · Validation côté client (longueurs min/max)
 *   · Honeypot field "website" caché (anti-bot)
 *   · Consentement RGPD obligatoire
 *
 * Backend : POST /api/public/contact (déclenche 2 emails auto)
 */
export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    consent: true,
    website: '', // honeypot
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverMsg, setServerMsg] = useState('');

  const update = (field) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value;
    setForm((f) => ({ ...f, [field]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setServerMsg('');

    // Validations client
    if (form.name.trim().length < 2) {
      setStatus('error');
      setServerMsg('Merci de renseigner ton nom (min. 2 caractères).');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setStatus('error');
      setServerMsg('Merci de saisir une adresse email valide.');
      return;
    }
    if (form.message.trim().length < 10) {
      setStatus('error');
      setServerMsg('Ton message est trop court (min. 10 caractères).');
      return;
    }
    if (!form.consent) {
      setStatus('error');
      setServerMsg('Le consentement RGPD est requis pour traiter ta demande.');
      return;
    }

    try {
      const res = await api.publicContact({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      setStatus('success');
      setServerMsg(res?.message || 'Merci ! Ta demande a bien été reçue.');
      // Reset form
      setForm({ name: '', email: '', subject: '', message: '', consent: true, website: '' });
    } catch (err) {
      setStatus('error');
      setServerMsg(err?.message || "Une erreur s'est produite. Merci de réessayer.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12" data-testid="contact-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors mb-6"
        data-testid="contact-back-home"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour à l'accueil
      </Link>

      {/* Header éditorial */}
      <div className="mb-8 sm:mb-10">
        <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.32em] text-[hsl(var(--primary))] mb-3">
          <span className="h-px w-6 bg-[hsl(var(--primary))]" />
          Nous écrire
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-[40px] font-black leading-[1.1] tracking-tight" data-testid="contact-title">
          Une question ? <span className="text-[hsl(var(--primary))]">On t'écoute.</span>
        </h1>
        <p className="mt-3 text-base text-foreground/75 leading-relaxed">
          Question pédagogique, suggestion d'amélioration, signalement d'un bug, partenariat
          enseignant… Notre équipe te répond sous 48h ouvrées.
        </p>
      </div>

      {/* Encart info rapide */}
      <div className="mc-card p-4 sm:p-5 mb-8 grid sm:grid-cols-3 gap-4 sm:gap-5" data-testid="contact-info-card">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(var(--primary-soft))' }}>
            <Mail className="w-4 h-4 text-[hsl(var(--primary))]" />
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Email</div>
            <div className="text-sm font-semibold mt-0.5">contact@code2roues.fr</div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(var(--primary-soft))' }}>
            <MessageSquare className="w-4 h-4 text-[hsl(var(--primary))]" />
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Délai</div>
            <div className="text-sm font-semibold mt-0.5">Sous 48h ouvrées</div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(var(--primary-soft))' }}>
            <User className="w-4 h-4 text-[hsl(var(--primary))]" />
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Équipe</div>
            <div className="text-sm font-semibold mt-0.5">Moniteurs ECSR</div>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={submit} className="mc-card p-5 sm:p-7 space-y-5" data-testid="contact-form" noValidate>
        {/* Honeypot caché — les bots le remplissent, pas les humains */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
          <label htmlFor="website">Ne pas remplir (site web) :</label>
          <input
            id="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update('website')}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
          <div className="space-y-1.5">
            <Label htmlFor="contact-name" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Ton nom *</Label>
            <Input
              id="contact-name"
              value={form.name}
              onChange={update('name')}
              placeholder="Marie Dupont"
              required
              minLength={2}
              maxLength={80}
              autoComplete="name"
              disabled={status === 'loading'}
              data-testid="contact-input-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Ton email *</Label>
            <Input
              id="contact-email"
              type="email"
              value={form.email}
              onChange={update('email')}
              placeholder="marie@exemple.fr"
              required
              autoComplete="email"
              disabled={status === 'loading'}
              data-testid="contact-input-email"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="contact-subject" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sujet</Label>
          <Input
            id="contact-subject"
            value={form.subject}
            onChange={update('subject')}
            placeholder="Question pédagogique, signalement, partenariat…"
            maxLength={120}
            disabled={status === 'loading'}
            data-testid="contact-input-subject"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="contact-message" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Ton message *</Label>
          <Textarea
            id="contact-message"
            value={form.message}
            onChange={update('message')}
            placeholder="Décris ta demande en quelques lignes…"
            required
            minLength={10}
            maxLength={5000}
            rows={6}
            disabled={status === 'loading'}
            data-testid="contact-input-message"
            className="resize-y min-h-[140px]"
          />
          <div className="text-[11px] text-muted-foreground text-right">
            {form.message.length} / 5000
          </div>
        </div>

        {/* Consentement RGPD */}
        <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer" data-testid="contact-consent-label">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={update('consent')}
            disabled={status === 'loading'}
            className="mt-0.5 w-4 h-4 accent-[hsl(var(--primary))] cursor-pointer"
            data-testid="contact-consent-checkbox"
          />
          <span className="leading-relaxed">
            J'accepte que mes informations soient utilisées <strong className="text-foreground">uniquement pour traiter ma demande</strong>.
            Aucun envoi commercial. Consulte notre <Link to="/confidentialite" className="underline hover:text-[hsl(var(--primary))]">politique de confidentialité</Link>.
          </span>
        </label>

        {/* Feedback messages */}
        {status === 'error' && serverMsg && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] text-sm" data-testid="contact-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{serverMsg}</span>
          </div>
        )}
        {status === 'success' && (
          <div className="flex items-start gap-2 p-4 rounded-md bg-green-500/10 text-green-700 dark:text-green-400 text-sm border border-green-500/30" data-testid="contact-success">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span><strong>Bien reçu !</strong> {serverMsg}</span>
          </div>
        )}

        <Button
          type="submit"
          size="lg"
          className="w-full sm:w-auto"
          disabled={status === 'loading' || status === 'success'}
          data-testid="contact-submit-btn"
        >
          {status === 'loading' ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi en cours…</>
          ) : status === 'success' ? (
            <><Check className="w-4 h-4 mr-2" />Envoyé !</>
          ) : (
            <><Send className="w-4 h-4 mr-2" />Envoyer ma demande</>
          )}
        </Button>
      </form>

      {/* Footer info */}
      <p className="text-xs text-muted-foreground mt-6 text-center leading-relaxed">
        Pour les enseignants ECSR, candidate plutôt via{' '}
        <Link to="/login" className="underline hover:text-[hsl(var(--primary))]">l'espace dédié</Link>{' '}
        (formulaire de candidature avec code d'accès).
      </p>
    </div>
  );
}
